// External packages
import * as React from 'react';

// Components
import { Checkbox } from 'components/Checkbox';
import { Icon } from 'components/Icons';
import { SearchField } from 'components/SearchField';
import { Option, Select } from 'components/Select';

// Modules
import { BulkActionsToolbar } from 'modules/BulkActionsToolbar';

// Hooks
import { useDeleteMedia, useGetAllMedia } from 'api/media';
import { useBulkSelection } from 'hooks/useBulkSelection';

// Types
import type { Media } from '../../../../server/src/types/media.types';

// Utils
import { filterAudio } from 'utils/filterAudio';
import { sortMedia } from 'utils/sortMedia';

export const AudioList = () => {
  const [search, setSearch] = React.useState('');
  const [sortBy, setSortBy] = React.useState<string>('datum');
  const [modalOpen, setModalOpen] = React.useState(false);

  const { data: media, isLoading, isError } = useGetAllMedia('audio');
  const { mutate: deleteMedia, isPending: isDeleting } = useDeleteMedia();

  const filteredMedia = React.useMemo(
    () => filterAudio(media, search),
    [media, search]
  );

  const sortedMedia = React.useMemo(
    () => sortMedia(filteredMedia, sortBy),
    [filteredMedia, sortBy]
  );

  const bulkSelection = useBulkSelection(sortedMedia);

  const handleBulkDelete = () => {
    bulkSelection.selectedIds.forEach((id) => {
      deleteMedia({ fileType: 'audio', id });
    });
    bulkSelection.clearSelection();
    setModalOpen(false);
  };

  return (
    <>
      {media && media.length > 0 && (
        <>
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <SearchField
              value={search}
              onChange={setSearch}
              placeholder="Pretraži audio..."
            />
            <Select
              placeholder="Sortiraj po"
              className="w-auto"
              selectedKey={sortBy}
              onSelectionChange={(key) => setSortBy(key as string)}
            >
              <Option id="datum">Dodano (najnovije)</Option>
              <Option id="datum-desc">Dodano (najstarije)</Option>
              <Option id="naziv">Naziv (A-B)</Option>
              <Option id="naziv-desc">Naziv (Z-A)</Option>
            </Select>
          </div>

          <BulkActionsToolbar
            allSelected={!!bulkSelection.allSelected}
            someSelected={!!bulkSelection.someSelected}
            selectedCount={bulkSelection.selectedIds.length}
            onSelectAll={bulkSelection.handleSelectAll}
            onBulkDelete={handleBulkDelete}
            isDeleteModalOpen={modalOpen}
            setDeleteModalOpen={setModalOpen}
            isDeleting={isDeleting}
            label="Odaberi sve"
          />
        </>
      )}

      {isLoading || isError || !sortedMedia || sortedMedia.length === 0 ? (
        <div className="flex items-center justify-center h-full">
          {isLoading ? (
            <Icon name="loader" className="animate-spin size-10" />
          ) : isError ? (
            <p>Greška pri učitavanju audio zapisa.</p>
          ) : (
            <p>Nema pronađenih audio zapisa.</p>
          )}
        </div>
      ) : (
        <div className="flex flex-col gap-4 overflow-y-auto">
          {sortedMedia.map((audio: Media) => (
            <div
              key={audio.id}
              className="flex items-center overflow-x-auto shrink-0 justify-between gap-4 px-4 py-2 border border-gray-200 bg-white rounded-lg"
            >
              <Checkbox
                isSelected={bulkSelection.selectedIds.includes(audio.id)}
                onChange={(checked) =>
                  bulkSelection.handleSelect(audio.id, !!checked)
                }
              />
              <div className="shrink-0 flex-4 min-w-50">
                <p className="font-semibold text-sm shrink-0">ID</p>
                <p className="mb-4">{audio.id}</p>
                <p className="font-semibold text-sm shrink-0">Naziv</p>
                <p>{audio.originalFileName || audio.fileName}</p>
              </div>
              <audio controls className="w-full flex-3 min-w-75 shrink-0">
                <source src={audio.url} type={audio.mimeType} />
                Your browser does not support the audio element.
              </audio>
            </div>
          ))}
        </div>
      )}
    </>
  );
};
